import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Button, Typography, styled } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import heic2any from "heic2any";

const CircleContainer = styled("div")({
  width: 150,
  height: 150,
  borderRadius: "50%",
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#555",
  fontSize: "14px",
  cursor: "pointer",
  position: "relative",
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "50%",
  position: "absolute",
  top: 0,
  left: 0,
});

const UpdateAdministrationDetail = () => {
  const jwtToken = localStorage.getItem("jwt_token");
  const { search } = useLocation();
  const history = useHistory();

  const paramIdSeprator = (param) => param.split("=")[1];

  const [userDetail, setUserDetail] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    organization_id: "",
    employee_id: "",
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());

  const addCacheBuster = (url) => {
    if (!url) return url;
    if (url.startsWith("data:")) return url;
    if (url.startsWith("blob:")) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}t=${imageKey}`;
  };

  useEffect(() => {
    getAdministrationDetail();
  }, []);

  const handleImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let finalFile = file;
      if (file.type === "image/heic" || file.type === "image/heif") {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });
        finalFile = new File(
          [convertedBlob],
          file.name.replace(/\.[^/.]+$/, ".jpeg"),
          { type: "image/jpeg" }
        );
      }

      setSelectedImage(finalFile);
      setImagePreviewUrl(URL.createObjectURL(finalFile));
      setImageKey(Date.now());
    } catch (error) {
      console.error("Image conversion failed:", error);
      toast.error("Unsupported image format.");
    }
  };

  const handleRemoveImage = (e) => {
    e.preventDefault();
    setSelectedImage(null);
    setImagePreviewUrl("");
    setImageKey(Date.now());
  };

  const onChangeHandler = (e) => {
    setUserDetail({ ...userDetail, [e.target.id]: e.target.value });
  };

  const getAdministrationDetail = async () => {
    const id = paramIdSeprator(search);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/${id}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      const data = await res.json();
      if (res.ok) {
        const {
          first_name,
          last_name,
          email,
          phone_number,
          organization_id,
          employee_id,
        } = data.data;
        setUserDetail({
          first_name,
          last_name,
          email,
          phone_number,
          organization_id,
          employee_id,
        });

        if (data.data.profile_image) {
          setImagePreviewUrl(data.data.profile_image);
          setImageKey(Date.now());
        }
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    const id = paramIdSeprator(search);

    let base64Image = null;
    if (selectedImage) {
      try {
        base64Image = await handleImageToBase64(selectedImage);
      } catch (err) {
        return toast.error("Image conversion failed.");
      }
    }

    const body = {
      ...userDetail,
      previous_org_id: localStorage.getItem("organisation_id"),
      profile_image: base64Image,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/${id}`,
        {
          method: "POST",
          body: JSON.stringify(body),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      const result = await res.json();
      if (res.ok) {
        if (result.data?.profile_image) {
          setImagePreviewUrl(result.data.profile_image);
          setImageKey(Date.now());
        }

        toast.success(result.message);

        setTimeout(() => {
          history.push("/administration");
        }, 500);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <form onSubmit={onSubmitHandler}>
      <h6 className="mb-3">Update Administration Details</h6>

      <Box
        mt={2}
        mb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Profile Photo:
        </Typography>
        <input
          accept="image/*"
          type="file"
          id="upload-image"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
        <label
          htmlFor="upload-image"
          style={{ position: "relative", display: "inline-block" }}
        >
          <CircleContainer>
            {imagePreviewUrl ? (
              <ImagePreview
                key={imageKey}
                src={addCacheBuster(imagePreviewUrl)}
                alt="Profile Preview"
              />
            ) : (
              <span>Upload your profile photo.</span>
            )}
          </CircleContainer>
          {imagePreviewUrl && (
            <Button
              onClick={handleRemoveImage}
              sx={{
                minWidth: "unset",
                padding: 0,
                position: "absolute",
                top: "10px",
                right: "-10px",
                backgroundColor: "#f44336",
                color: "#fff",
                borderRadius: "50%",
                width: 28,
                height: 28,
                "&:hover": {
                  backgroundColor: "#d32f2f",
                },
              }}
            >
              <RxCross2 size={16} />
            </Button>
          )}
        </label>
      </Box>

      <div className="row">
        {[
          "first_name",
          "last_name",
          "email",
          "phone_number",
          "organization_id",
          "employee_id",
        ].map((field) => (
          <div className="col-md-6 mt-2" key={field}>
            <label htmlFor={field}>
              {field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              {field !== "employee_id" && field !== "phone_number" && (
                <span className="danger">*</span>
              )}
            </label>
            <input
              type={
                field === "email"
                  ? "email"
                  : field === "phone_number"
                  ? "number"
                  : "text"
              }
              className="form-control"
              id={field}
              required={field !== "employee_id" && field !== "phone_number"}
              value={userDetail[field]}
              onChange={onChangeHandler}
            />
          </div>
        ))}
      </div>

      <button type="submit" className="btn my-3 bg_color">
        Save Changes
      </button>
    </form>
  );
};

export default UpdateAdministrationDetail;
